// src/pages/VerificationRequired.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Typography, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { Mail, Refresh, Logout, CheckCircle, Error } from '@mui/icons-material';
import api from '../api/axios';
import { BrandLogo } from '../branding/BrandAsset';

const VerificationRequired = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // Get email from location state or localStorage
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem('verificationEmail');
    const finalEmail = stateEmail || storedEmail || '';
    setEmail(finalEmail);

    // Check if already verified
    if (finalEmail) {
      checkVerificationStatus(finalEmail);
    }

    // Clear any auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
  }, [location]);

  const checkVerificationStatus = async (userEmail) => {
    setChecking(true);
    try {
      const response = await api.get(`/auth/check-verification?email=${userEmail}`);
      if (response.data.success && response.data.data.isVerified) {
        setIsVerified(true);
        // Clear verification email from storage
        localStorage.removeItem('verificationEmail');
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified! Please login to continue.',
              severity: 'success'
            } 
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Check verification error:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      const newEmail = prompt('Please enter your email address:');
      if (!newEmail) return;
      setEmail(newEmail);
      localStorage.setItem('verificationEmail', newEmail);
    }

    setResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const response = await api.post('/auth/resend-verification', { email });
      
      if (response.data.success) {
        setResendMessage(response.data.message || 'Verification email sent! Please check your inbox.');
        // Check status after resend
        setTimeout(() => checkVerificationStatus(email), 3000);
      } else {
        setResendError(response.data.message || 'Failed to resend verification email');
        if (response.data.data?.alreadyVerified) {
          setResendMessage('Your email is already verified! Redirecting to login...');
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                message: 'Email already verified! Please login to continue.',
                severity: 'success'
              } 
            });
          }, 2000);
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.data?.alreadyVerified) {
        setResendMessage('Your email is already verified! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email already verified! Please login to continue.',
              severity: 'success'
            } 
          });
        }, 2000);
        return;
      }
      setResendError(errorData?.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          padding: 2,
        }}
      >
        <CircularProgress sx={{ color: '#000080', mb: 3 }} size={60} />
        <Typography variant="h6" sx={{ color: '#333' }}>
          Checking verification status...
        </Typography>
      </Box>
    );
  }

  if (isVerified) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 480,
            width: '100%',
            padding: 4,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <CheckCircle sx={{ fontSize: 80, color: '#2ecc71', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1a2e' }}>
            Email Verified! 🎉
          </Typography>
          <Typography variant="body1" sx={{ color: '#4a4a5a', mb: 3 }}>
            Your email has been verified. Redirecting to login...
          </Typography>
          <CircularProgress size={30} sx={{ color: '#000080' }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 500,
          width: '100%',
          padding: { xs: 3, sm: 4 },
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3 }}>
          <BrandLogo alt="ProTradesInsight" style={{ height: 50, maxWidth: '100%', objectFit: 'contain' }} />
        </Box>

        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,128,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
        >
          <Mail sx={{ fontSize: 40, color: '#000080' }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1a2e' }}>
          Verify Your Email
        </Typography>

        <Typography variant="body1" sx={{ color: '#4a4a5a', mb: 2, lineHeight: 1.7 }}>
          We've sent a verification link to <strong>{email || 'your email'}</strong>.
          Please check your inbox and click the link to verify your account.
        </Typography>

        {location.state?.message && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
            {location.state.message}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
          <Typography variant="body2">
            <strong>Can't find the email?</strong> Check your spam folder or request a new link below.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleResendVerification}
            disabled={resending}
            startIcon={!resending && <Refresh />}
            fullWidth
            sx={{
              backgroundColor: '#000080',
              '&:hover': { backgroundColor: '#0000a0' },
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {resending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                Sending...
              </>
            ) : (
              'Resend Verification Email'
            )}
          </Button>

          {resendMessage && (
            <Alert 
              severity={resendMessage.includes('verified') || resendMessage.includes('sent') ? 'success' : 'info'}
              sx={{ borderRadius: 2 }}
            >
              {resendMessage}
            </Alert>
          )}

          {resendError && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {resendError}
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={handleLogout}
            startIcon={<Logout />}
            fullWidth
            sx={{
              borderColor: '#e74c3c',
              color: '#e74c3c',
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#c0392b',
                backgroundColor: 'rgba(231, 76, 60, 0.05)',
              },
            }}
          >
            Logout
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Need help? Contact us at{' '}
            <a href="mailto:info@protradesinsight.com" style={{ color: '#000080' }}>
              info@protradesinsight.com
            </a>
          </Typography>
        </Box>

        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e8e8e8' }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            &copy; {new Date().getFullYear()} ProTradesInsight. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default VerificationRequired;
